import type {Map} from 'immutable';
import constants from 'client/constants';
import type {AuthoringSupplement as AuthoringSupplementModel} from 'resources/GeneratedModels/AuthoringSupplement/AuthoringSupplementModel.v1';
import type {Supplement as SupplementModel} from 'resources/GeneratedModels/Supplement/SupplementModel.v1';

type Location = {
  path: string;
  host: string;
  href: string;
};

/**
 * Shape of the model instance these extensions are mixed into. The extensions
 * rely on methods provided by the base Supplement/AuthoringSupplement models.
 */
interface BaseSupplementContext {
  getContent(): Map<string, unknown>;
  getType(): string;
  setField(field: string, value: unknown): AuthoringSupplementModel | SupplementModel;
}

const requiredAttributesByType: Record<string, string[]> = {
  audio: ['audio'],
  'free-form': ['text'],
  image: ['image'],
  passage: ['text'],
  'sortable-table': ['text']
};

export const baseSupplementExtensions = {
  getAlttext(this: BaseSupplementContext): string {
    return this.getContent().get('alttext') as string;
  },

  getAudio(this: BaseSupplementContext): string {
    return this.getContent().get('audio') as string;
  },

  getCaption(this: BaseSupplementContext): string {
    return this.getContent().get('caption') as string;
  },

  getCropValues(this: BaseSupplementContext): Map<string, unknown> {
    const keySet = ['x', 'y', 'width', 'height'];
    // Immutable 3.8 typings declare Map.filter as returning the broader
    // `Iterable<K, V>`, but at runtime filtering a Map yields a Map.
    return this.getContent().filter((value, key) =>
      key !== undefined && keySet.includes(key)
    ) as Map<string, unknown>;
  },

  getExcludeWhiteSpace(this: BaseSupplementContext): boolean {
    return this.getContent().get('excludeWhiteSpace') as boolean;
  },

  getHasTotal(this: BaseSupplementContext): boolean {
    return this.getContent().get('hasTotal') as boolean;
  },

  getHideLineNumbers(this: BaseSupplementContext): boolean {
    return this.getContent().get('hideLineNumbers') as boolean;
  },

  getIgnoreWhiteSpace(this: BaseSupplementContext): boolean {
    return this.getContent().get('ignoreWhiteSpace') as boolean;
  },

  /**
   * `getImageSize` is the scale percentage of the original asset (0-100).
   */
  getImageSize(this: BaseSupplementContext): string {
    return this.getContent().get('imageSize', 100) as string;
  },

  getImageUrl(this: BaseSupplementContext): string {
    return this.getContent().get('image') as string;
  },
  /**
   * Returns a `Location` object representing the location of the supplement.
   */
  getImageLocation(this: BaseSupplementContext): Location {
    const origin = constants.SUPPLEMENTS_ORIGIN;

    // This is necessary to encode URLs of older image supplements names that are not normalized
    const encodedPath = (this.getContent().get('image') as string | undefined)
      ?.split('/')
      .map(encodeURIComponent)
      .join('/');

    return {
      path: encodedPath,
      href: `${origin}/${encodedPath}`
    } as Location;
  },
  getGenerationMode(this: BaseSupplementContext): string | null {
    return this.getContent().get('generationMode', null) as string | null;
  },

  getImagePrompt(this: BaseSupplementContext): string {
    return this.getContent().get('imagePrompt') as string;
  },

  getImageStatus(this: BaseSupplementContext): string {
    return this.getContent().get('imageStatus', 'complete') as string;
  },

  getAudioText(this: BaseSupplementContext): string {
    return this.getContent().get('audioText') as string;
  },

  getAudioStatus(this: BaseSupplementContext): string {
    return this.getContent().get('audioStatus', 'complete') as string;
  },

  getVoiceId(this: BaseSupplementContext): string | null {
    return this.getContent().get('voiceId', null) as string | null;
  },

  getLanguageCode(this: BaseSupplementContext): string | null {
    return this.getContent().get('languageCode', null) as string | null;
  },

  getText(this: BaseSupplementContext): string {
    return this.getContent().get('text') as string;
  },

  getType(this: BaseSupplementContext): string {
    return this.getContent().get('type') as string;
  },

  getValueForContentAttribute(
    this: BaseSupplementContext,
    attribute: string,
    def: unknown
  ): unknown {
    return this.getContent().get(attribute, def);
  },

  isAttributeRequired(this: BaseSupplementContext, attribute: string): boolean {
    if (!this.getType()) {
      return false;
    }
    return requiredAttributesByType[this.getType()].includes(attribute);
  },

  setAlttext(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('alttext', value);
    return this.setField('content', content);
  },

  setAudio(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('audio', value);
    return this.setField('content', content);
  },

  setCaption(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('caption', value);
    return this.setField('content', content);
  },

  setCropValues(
    this: BaseSupplementContext,
    value: {
      x: number;
      y: number;
      width: number;
      height: number;
    }
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().merge(value);
    return this.setField('content', content);
  },

  setExcludeWhiteSpace(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('excludeWhiteSpace', value);
    return this.setField('content', content);
  },

  setHasTotal(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('hasTotal', value);
    return this.setField('content', content);
  },

  setHideLineNumbers(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('hideLineNumbers', value);
    return this.setField('content', content);
  },

  setShowLineNumbers(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('hideLineNumbers', !value);
    return this.setField('content', content);
  },

  setIgnoreWhiteSpace(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('ignoreWhiteSpace', value);
    return this.setField('content', content);
  },

  setShowWhiteSpace(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('ignoreWhiteSpace', !value);
    return this.setField('content', content);
  },

  setImageSize(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('imageSize', value);
    return this.setField('content', content);
  },

  setGenerationMode(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('generationMode', value);
    return this.setField('content', content);
  },

  setImagePrompt(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('imagePrompt', value);
    return this.setField('content', content);
  },

  setImageUrl(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent()
      .set('image', value)
      // if we manually set an image URL, clear out any existing prompt
      .set('imagePrompt', '');
    return this.setField('content', content);
  },

  setAudioText(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('audioText', value);
    return this.setField('content', content);
  },

  setVoiceId(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('voiceId', value || null);
    return this.setField('content', content);
  },

  setLanguageCode(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('languageCode', value || null);
    return this.setField('content', content);
  },

  setText(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('text', value);
    return this.setField('content', content);
  },

  setType(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('type', value);
    return this.setField('content', content);
  },

  setLineNumberIncrement(
    this: BaseSupplementContext,
    value: number
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('lineNumberIncrement', value);
    return this.setField('content', content);
  },

  setLineNumberAlignment(
    this: BaseSupplementContext,
    value: string
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('lineNumberAlignment', value);
    return this.setField('content', content);
  },

  setExcludeHeaders(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('excludeHeaders', value);
    return this.setField('content', content);
  },

  setSetMaxHeight(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('setMaxHeight', value);
    return this.setField('content', content);
  },

  setTitle(
    this: BaseSupplementContext,
    value: string
  ): AuthoringSupplementModel | SupplementModel {
    const content = this.getContent().set('title', value);
    return this.setField('content', content);
  }
};
